import React, { useEffect, useRef, useState } from 'react';
import {
  CheckOutlined,
  CloseOutlined,
  PlusOutlined,
  CaretRightFilled,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import type { TicketProposal } from '@nxsys/types/models/AiChat';

type Props = {
  proposal: TicketProposal;
  isRaising: boolean;
  onConfirm: (attachments?: string[], notes?: string, captureScreen?: boolean) => void;
  onDismiss: () => void;
};

type Attachment = { dataUrl: string; isVideo: boolean; name: string };

const MAX_FILES = 10;

// Design tokens (handoff 1d) — final values, keep in sync with the app theme.
const PA = '#00795B';
const PA_TINT = '#f3fffc';
const BORDER = '#0000001f';
const INSET = '#FBFCFC';
const TITLE = '#16171A';
const BODY = '#23282E';
const SECONDARY = '#5C656E';
const MUTED = '#8F9BA6';

const TicketProposalCard: React.FC<Props> = ({ proposal, isRaising, onConfirm, onDismiss }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [notes, setNotes] = useState('');
  const [captureScreen, setCaptureScreen] = useState(true);

  // Reset card-local state whenever a different proposal is shown.
  useEffect(() => {
    setFiles([]);
    setNotes('');
    setCaptureScreen(true);
  }, [proposal.subject, proposal.description]);

  const addFile = (file: File) => {
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) return;
    const reader = new FileReader();
    reader.onload = () =>
      setFiles((prev) =>
        prev.length >= MAX_FILES
          ? prev
          : [
              ...prev,
              {
                dataUrl: reader.result as string,
                isVideo: file.type.startsWith('video/'),
                name: file.name,
              },
            ],
      );
    reader.readAsDataURL(file);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items).filter(
      (i) => i.type.startsWith('image/') || i.type.startsWith('video/'),
    );
    if (items.length) {
      e.preventDefault();
      items.forEach((i) => {
        const f = i.getAsFile();
        if (f) addFile(f);
      });
    }
  };

  const hasFiles = files.length > 0;
  const isAppend = Boolean(proposal.appendToTicketNumber);

  const openPicker = () => fileInputRef.current?.click();
  const submit = () =>
    onConfirm(hasFiles ? files.map((f) => f.dataUrl) : undefined, notes.trim() || undefined, captureScreen);

  return (
    <div style={{ alignSelf: 'flex-start', width: '100%', margin: '4px 0' }}>
      <div
        onPaste={handlePaste}
        style={{
          border: `1px solid ${BORDER}`,
          borderRadius: 14,
          background: '#fff',
          overflow: 'hidden',
          boxShadow: '0 2px 10px rgba(0,121,91,.06)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '13px 15px',
            background: `linear-gradient(180deg, ${PA_TINT}, #fff)`,
          }}
        >
          <span
            style={{
              flex: 'none',
              width: 34,
              height: 34,
              borderRadius: 9,
              background: PA,
              display: 'grid',
              placeItems: 'center',
              boxShadow: '0 2px 6px rgba(0,121,91,.25)',
            }}
          >
            <CustomerServiceOutlined style={{ fontSize: 17, color: '#fff' }} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: TITLE, lineHeight: 1.35 }}>
              {proposal.subject}
            </div>
            <div style={{ fontSize: 11, color: SECONDARY, marginTop: 2 }}>
              {isAppend ? (
                <>
                  Will be added to ticket{' '}
                  <b style={{ color: PA }}>#{proposal.appendToTicketNumber}</b>
                </>
              ) : (
                <>
                  Will be sent to the <b style={{ color: PA }}>Support team</b>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {proposal.description ? (
          <div
            style={{
              padding: '11px 15px 0',
              fontSize: 12.5,
              lineHeight: 1.55,
              color: SECONDARY,
              whiteSpace: 'pre-wrap',
            }}
          >
            {proposal.description}
          </div>
        ) : null}

        {/* Body: note + attachments + capture checkbox */}
        <div style={{ padding: '11px 15px' }}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add a note for the support team (optional)"
            maxLength={2000}
            disabled={isRaising}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              resize: 'none',
              fontFamily: 'inherit',
              fontSize: 13,
              lineHeight: 1.5,
              color: BODY,
              border: `1px solid ${BORDER}`,
              borderRadius: 9,
              padding: '9px 11px',
              minHeight: 44,
              outline: 'none',
              background: INSET,
            }}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              Array.from(e.target.files || []).forEach(addFile);
              e.target.value = '';
            }}
          />

          {/* Attachments strip (user-added files only) */}
          {hasFiles ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {files.map((f, i) => (
                <div
                  key={i}
                  style={{
                    position: 'relative',
                    width: 64,
                    height: 52,
                    borderRadius: 9,
                    overflow: 'hidden',
                    border: `1px solid ${BORDER}`,
                    background: f.isVideo
                      ? 'linear-gradient(135deg,#2F3B37,#0F1714)'
                      : '#000',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  {f.isVideo ? (
                    <CaretRightFilled style={{ fontSize: 18, color: '#fff' }} />
                  ) : (
                    <img
                      src={f.dataUrl}
                      alt={f.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )}
                  <span
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      fontSize: 8,
                      fontWeight: 600,
                      color: '#fff',
                      background: 'rgba(15,23,20,.62)',
                      padding: '2px 5px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {f.name}
                  </span>
                  {!isRaising && (
                    <span
                      title="Remove"
                      onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      style={{
                        position: 'absolute',
                        top: 3,
                        right: 3,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: 'rgba(15,23,20,.7)',
                        display: 'grid',
                        placeItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <CloseOutlined style={{ fontSize: 9, color: '#fff' }} />
                    </span>
                  )}
                </div>
              ))}
              {files.length < MAX_FILES && (
                <button
                  type="button"
                  title="Add files"
                  disabled={isRaising}
                  onClick={openPicker}
                  style={{
                    width: 64,
                    height: 52,
                    borderRadius: 9,
                    border: `1px dashed #C3CBD2`,
                    background: INSET,
                    color: MUTED,
                    cursor: 'pointer',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <PlusOutlined style={{ fontSize: 16 }} />
                </button>
              )}
            </div>
          ) : null}

          {/* Capture-screen checkbox row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: hasFiles ? 10 : 9 }}>
            <label
              onClick={() => !isRaising && setCaptureScreen((v) => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', flex: 1, minWidth: 0 }}
            >
              <span
                style={{
                  flex: 'none',
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  background: captureScreen ? PA : '#fff',
                  border: captureScreen ? 'none' : `1px solid ${BORDER}`,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {captureScreen && <CheckOutlined style={{ fontSize: 11, color: '#fff' }} />}
              </span>
              <span style={{ fontSize: 11.5, color: SECONDARY, lineHeight: 1.3 }}>
                Capture my screen when I create the ticket
              </span>
            </label>
            {!hasFiles && (
              <span
                onClick={() => !isRaising && openPicker()}
                style={{
                  flex: 'none',
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: PA,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                }}
              >
                Add files
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '11px 15px',
            borderTop: `1px solid ${BORDER}`,
            background: INSET,
          }}
        >
          <button
            type="button"
            disabled={isRaising}
            onClick={submit}
            style={{
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 500,
              padding: '8px 16px',
              border: 'none',
              borderRadius: 9,
              background: PA,
              color: '#fff',
              cursor: isRaising ? 'default' : 'pointer',
              opacity: isRaising ? 0.75 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
            }}
          >
            <CheckOutlined style={{ fontSize: 14 }} />
            {isRaising ? 'Creating…' : isAppend ? 'Add note' : 'Create ticket'}
          </button>
          <button
            type="button"
            disabled={isRaising}
            onClick={onDismiss}
            style={{
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 500,
              padding: '8px 12px',
              border: 'none',
              background: 'transparent',
              color: SECONDARY,
              borderRadius: 9,
              cursor: isRaising ? 'default' : 'pointer',
              marginLeft: 'auto',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketProposalCard;
